import React, { useState, useEffect } from "react";
import "./dstyle.css";
import SideBar from "./SideBar";
import Navbar from "./Navbar";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faBookOpen,
  faGraduationCap,
  faTrophy,
  faChartPie,
  faChalkboardUser,
  faCheckCircle,
  faSpinner
} from "@fortawesome/free-solid-svg-icons";

function Dashboard() {
  const navigate = useNavigate();
  const adminId = localStorage.getItem("adminId");
  const adminRole = localStorage.getItem("adminRole");

  const [usersCount, setUsersCount] = useState(0);
  const [coursesCount, setCoursesCount] = useState(0);
  const [enrolledCount, setEnrolledCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [courseAnalytics, setCourseAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!adminId) { navigate("/admin-login"); return; }
    async function fetchAdminData() {
      try {
        const [usersRes, coursesRes, learningRes, assessmentsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/users`).then((r) => r.json()),
          fetch(`${API_BASE_URL}/api/courses`).then((r) => r.json()),
          fetch(`${API_BASE_URL}/api/learning`).then((r) => r.json()),
          fetch(`${API_BASE_URL}/api/assessments`).then((r) => r.json())
        ]);

        const usersList = Array.isArray(usersRes) ? usersRes : [];
        const coursesList = Array.isArray(coursesRes) ? coursesRes : [];
        const learningList = Array.isArray(learningRes) ? learningRes : [];
        const assessmentsList = Array.isArray(assessmentsRes) ? assessmentsRes : [];

        setUsersCount(usersList.length);
        setCoursesCount(coursesList.length);
        setEnrolledCount(learningList.length);

        // Filter passed assessments (marks >= 90)
        const passedAssessments = assessmentsList.filter((a) => (a.marks || 0) >= 90);
        setCompletedCount(passedAssessments.length);

        // Compute per-course analytics
        const analytics = coursesList.map((course) => {
          const cId = course.course_id || course.id;
          const courseEnrolled = learningList.filter((l) => (l.course_id || l.courseId) == cId);
          const coursePassed = passedAssessments.filter((a) => (a.course_id || a.courseId) == cId);
          const enrolledCnt = courseEnrolled.length;
          const passedCnt = coursePassed.length;
          const completionRate = enrolledCnt > 0 ? Math.round((passedCnt / enrolledCnt) * 100) : 0;

          return {
            id: cId,
            name: course.course_name || course.courseName || `Course #${cId}`,
            instructor: course.instructor || course.tutor || "Faculty",
            price: course.price || 0,
            enrolledCount: enrolledCnt,
            completedCount: passedCnt,
            completionRate
          };
        });

        setCourseAnalytics(analytics);
        setLoading(false);
      } catch (err) {
        console.error("Error loading dashboard analytics:", err);
        setLoading(false);
      }
    }

    fetchAdminData();
  }, []);

  return (
    <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      <SideBar current={"dashboard"} />
      <section id="content">
        <Navbar />
        <main style={{ padding: "28px" }}>
          {/* Header */}
          <div style={{ marginBottom: "28px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
            <div>
              <h1 style={{ color: "#0f172a", fontFamily: "'Outfit', sans-serif", fontSize: "1.8rem", fontWeight: 800, margin: 0 }}>
                Consortium Admin Dashboard
              </h1>
              <p style={{ color: "#64748b", margin: "4px 0 0", fontSize: "0.92rem" }}>
                Real-time platform analytics, student enrollments, and course performance metrics
              </p>
            </div>
          </div>

          {/* Stat Cards Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "20px",
              marginBottom: "32px"
            }}
          >
            {/* Total Users */}
            <div
              style={{
                background: "#fff",
                borderRadius: "16px",
                padding: "24px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.03)",
                display: "flex",
                alignItems: "center",
                gap: "18px"
              }}
            >
              <div
                style={{
                  width: "52px",
                  height: "52px",
                  borderRadius: "14px",
                  background: "#e0e7ff",
                  color: "#4f46e5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.3rem"
                }}
              >
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "1.7rem", fontWeight: 800, color: "#0f172a" }}>{usersCount}</h3>
                <p style={{ margin: 0, color: "#64748b", fontSize: "0.85rem", fontWeight: 600 }}>Total Registered Users</p>
              </div>
            </div>

            {/* Total Courses */}
            <div
              style={{
                background: "#fff",
                borderRadius: "16px",
                padding: "24px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.03)",
                display: "flex",
                alignItems: "center",
                gap: "18px"
              }}
            >
              <div
                style={{
                  width: "52px",
                  height: "52px",
                  borderRadius: "14px",
                  background: "#cff4fc",
                  color: "#0891b2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.3rem"
                }}
              >
                <FontAwesomeIcon icon={faBookOpen} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "1.7rem", fontWeight: 800, color: "#0f172a" }}>{coursesCount}</h3>
                <p style={{ margin: 0, color: "#64748b", fontSize: "0.85rem", fontWeight: 600 }}>Active Courses</p>
              </div>
            </div>

            {/* Total Enrollments */}
            <div
              style={{
                background: "#fff",
                borderRadius: "16px",
                padding: "24px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.03)",
                display: "flex",
                alignItems: "center",
                gap: "18px"
              }}
            >
              <div
                style={{
                  width: "52px",
                  height: "52px",
                  borderRadius: "14px",
                  background: "#fef3c7",
                  color: "#d97706",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.3rem"
                }}
              >
                <FontAwesomeIcon icon={faGraduationCap} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "1.7rem", fontWeight: 800, color: "#0f172a" }}>{enrolledCount}</h3>
                <p style={{ margin: 0, color: "#64748b", fontSize: "0.85rem", fontWeight: 600 }}>Total Student Enrollments</p>
              </div>
            </div>

            {/* Total Certifications / Completions */}
            <div
              style={{
                background: "#fff",
                borderRadius: "16px",
                padding: "24px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.03)",
                display: "flex",
                alignItems: "center",
                gap: "18px"
              }}
            >
              <div
                style={{
                  width: "52px",
                  height: "52px",
                  borderRadius: "14px",
                  background: "#d1fae5",
                  color: "#059669",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.3rem"
                }}
              >
                <FontAwesomeIcon icon={faTrophy} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "1.7rem", fontWeight: 800, color: "#0f172a" }}>{completedCount}</h3>
                <p style={{ margin: 0, color: "#64748b", fontSize: "0.85rem", fontWeight: 600 }}>Earned Certificates (≥90%)</p>
              </div>
            </div>
          </div>

          {/* Detailed Course Analytics Table */}
          <div
            style={{
              background: "#fff",
              borderRadius: "20px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 6px -1px rgba(0,0,0,0.03)",
              overflow: "hidden"
            }}
          >
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #f1f5f9",
                background: "#fafbff",
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}
            >
              <FontAwesomeIcon icon={faChartPie} style={{ color: "#4f46e5", fontSize: "1.1rem" }} />
              <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#0f172a", fontFamily: "'Outfit', sans-serif" }}>
                Course Enrollment & Completion Analytics
              </h3>
            </div>

            <div style={{ overflowX: "auto" }}>
              {loading ? (
                <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b" }}>
                  <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "1.8rem", color: "#4f46e5", marginBottom: "10px" }} />
                  <p>Loading course analytics...</p>
                </div>
              ) : courseAnalytics.length === 0 ? (
                <p style={{ padding: "30px", color: "#94a3b8", textAlign: "center" }}>No courses created yet.</p>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "0.9rem" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", color: "#64748b", fontWeight: "700" }}>
                      <th style={{ padding: "14px 24px" }}>Course Title</th>
                      <th style={{ padding: "14px 24px" }}>Instructor</th>
                      <th style={{ padding: "14px 24px", textAlign: "center" }}>Enrolled Students</th>
                      <th style={{ padding: "14px 24px", textAlign: "center" }}>Completed (≥90%)</th>
                      <th style={{ padding: "14px 24px" }}>Completion Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courseAnalytics.map((item) => (
                      <tr key={item.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "16px 24px", fontWeight: "700", color: "#0f172a" }}>
                          {item.name}
                        </td>
                        <td style={{ padding: "16px 24px", color: "#475569" }}>
                          <FontAwesomeIcon icon={faChalkboardUser} style={{ marginRight: "6px", color: "#4f46e5" }} />
                          {item.instructor}
                        </td>
                        <td style={{ padding: "16px 24px", textAlign: "center" }}>
                          <span
                            style={{
                              background: "#e0e7ff",
                              color: "#4f46e5",
                              padding: "4px 14px",
                              borderRadius: "20px",
                              fontWeight: 700,
                              fontSize: "0.85rem"
                            }}
                          >
                            {item.enrolledCount} {item.enrolledCount === 1 ? "Student" : "Students"}
                          </span>
                        </td>
                        <td style={{ padding: "16px 24px", textAlign: "center" }}>
                          <span
                            style={{
                              background: "#d1fae5",
                              color: "#059669",
                              padding: "4px 14px",
                              borderRadius: "20px",
                              fontWeight: 700,
                              fontSize: "0.85rem"
                            }}
                          >
                            <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: "4px" }} />
                            {item.completedCount} Passed
                          </span>
                        </td>
                        <td style={{ padding: "16px 24px", minWidth: "180px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <div style={{ flex: 1, background: "#e2e8f0", borderRadius: "8px", height: "8px", overflow: "hidden" }}>
                              <div
                                style={{
                                  width: `${item.completionRate}%`,
                                  height: "100%",
                                  background: "linear-gradient(90deg, #4f46e5 0%, #10b981 100%)",
                                  borderRadius: "8px"
                                }}
                              />
                            </div>
                            <span style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.85rem" }}>
                              {item.completionRate}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </main>
      </section>
    </div>
  );
}

export default Dashboard;
